import { useMemo, useState, type FormEvent } from 'react';
import {
  partnersApi,
  subconInvoicesApi,
  type Partner,
  type PartnerWrite,
  type SubconInvoice,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { adminNavItems } from '@/pages/navItems';
import { useConfirm, useToast } from '@/components/Feedback';
import { formatDate, formatPeso } from '@/lib/format';
import { FlashMessage, errorText, useFlash } from '@/components/FlashMessage';

const EMPTY_FORM: PartnerWrite = {
  name: '',
  tin: '',
  contact: '',
  contractStart: '',
  contractEnd: '',
  status: 'active',
};

export function AdminPartners() {
  const { data: partners, loading, error: loadError, reload } = useAsync(() => partnersApi.list(), []);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Partner | null>(null);
  const [form, setForm] = useState<PartnerWrite>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const [error, setError] = useState('');

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowForm(true);
  };

  const openEdit = (row: Partner) => {
    setEditing(row);
    setForm({
      name: row.name,
      tin: row.tin ?? '',
      contact: row.contact ?? '',
      contractStart: row.contractStart ? row.contractStart.slice(0, 10) : '',
      contractEnd: row.contractEnd ? row.contractEnd.slice(0, 10) : '',
      status: row.status ?? 'active',
    });
    setError('');
    setShowForm(true);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload: PartnerWrite = {
        name: form.name.trim(),
        tin: form.tin || undefined,
        contact: form.contact || undefined,
        contractStart: form.contractStart ? new Date(form.contractStart).toISOString() : undefined,
        contractEnd: form.contractEnd ? new Date(form.contractEnd).toISOString() : undefined,
        status: form.status || 'active',
      };
      if (editing) {
        await partnersApi.update(editing.id, payload);
        toast(`${payload.name} updated`);
      } else {
        await partnersApi.create(payload);
        toast(`${payload.name} added`);
      }
      setShowForm(false);
      reload();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to save partner');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: 'name', header: 'Name' },
    {
      key: 'tin',
      header: 'TIN',
      value: (row: Partner) => row.tin ?? '',
      render: (row: Partner) => row.tin ?? '—',
    },
    {
      key: 'contact',
      header: 'Contact',
      value: (row: Partner) => row.contact ?? '',
      render: (row: Partner) => row.contact ?? '—',
    },
    {
      key: 'contract',
      header: 'Contract',
      value: (row: Partner) => row.contractStart ?? '',
      render: (row: Partner) =>
        row.contractStart
          ? `${formatDate(row.contractStart)} – ${row.contractEnd ? formatDate(row.contractEnd) : 'open-ended'}`
          : '—',
    },
    {
      key: 'status',
      header: 'Status',
      value: (row: Partner) => row.status ?? 'active',
      render: (row: Partner) => <StatusBadge status={row.status ?? 'active'} size="sm" />,
    },
    {
      key: 'accuracyScore',
      header: 'Match accuracy',
      value: (row: Partner) => row.accuracyScore ?? null,
      render: (row: Partner) => (row.accuracyScore != null ? `${row.accuracyScore}%` : '—'),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (row: Partner) => (
        <div className="row-actions">
          <Button size="sm" variant="ghost" onClick={() => openEdit(row)}>Edit</Button>
        </div>
      ),
    },
  ];

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <p className="kicker">Fleet</p>
            <h1>Subcontractors</h1>
            <p className="text-muted">
              Organisations supplying subcon vehicles — name, TIN, contact, and contract window
            </p>
          </div>
          <Button onClick={openAdd}>Add partner</Button>
        </div>


        <DataTable
          columns={columns}
          data={partners ?? []}
          keyField="id"
          loading={loading}
          loadError={loadError}
          dense
          searchPlaceholder="Filter partners…"
          emptyMessage="No partners yet. Add subcontractors before assigning subcon vehicles."
        />

        <SubconVarianceSection partners={partners ?? []} />

        <Modal
          open={showForm}
          onClose={() => setShowForm(false)}
          title={editing ? 'Edit partner' : 'Add partner'}
        >
          <form onSubmit={save}>
            <Input
              label="Partner name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              placeholder="Swift Logistics Partners Inc."
            />
            <Input
              label="TIN"
              value={form.tin ?? ''}
              onChange={(e) => setForm({ ...form, tin: e.target.value })}
              placeholder="000-000-000-000"
            />
            <Input
              label="Contact"
              value={form.contact ?? ''}
              onChange={(e) => setForm({ ...form, contact: e.target.value })}
              placeholder="Name · phone · email"
            />
            <div className="grid-2">
              <Input
                label="Contract start"
                type="date"
                value={form.contractStart ?? ''}
                onChange={(e) => setForm({ ...form, contractStart: e.target.value })}
              />
              <Input
                label="Contract end"
                type="date"
                value={form.contractEnd ?? ''}
                onChange={(e) => setForm({ ...form, contractEnd: e.target.value })}
              />
            </div>
            {editing && (
              <Select
                label="Status"
                value={form.status ?? 'active'}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            )}
            {error && <p className="text-urgent">{error}</p>}
            <Button type="submit" loading={saving}>{editing ? 'Save changes' : 'Create partner'}</Button>
          </form>
        </Modal>

        <style>{`
          .row-actions { display: flex; gap: 0.25rem; justify-content: flex-end; }
          .grid-2 {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: var(--space-md);
          }
        `}</style>
      </div>
    </Layout>
  );
}

const EMPTY_MATCH_FORM = {
  partnerId: '',
  invoiceNumber: '',
  periodStart: '',
  periodEnd: '',
  expectedTotal: '',
  actualTotal: '',
};

/** Minimal UI for the three-step subcon variance flow: step 1 (draft match)
 * happens on submit below and auto-resolves to matched/variance_review;
 * step 2/3 (the review decision) is the Approve/Dispute actions in the table. */
function SubconVarianceSection({ partners }: { partners: Partner[] }) {
  const { data: invoices, loading, error: invoicesLoadError, reload } = useAsync(() => subconInvoicesApi.list(), []);
  const { data: accuracy, reload: reloadAccuracy } = useAsync(() => subconInvoicesApi.partnerAccuracy(), []);

  const [form, setForm] = useState(EMPTY_MATCH_FORM);
  const [saving, setSaving] = useState(false);
  const { flash, ok: showOk, error: showError, clear: clearFlash } = useFlash();
  const toast = useToast();
  const confirm = useConfirm();
  const [decidingId, setDecidingId] = useState<string | null>(null);

  const partnerMap = useMemo(() => Object.fromEntries(partners.map((p) => [p.id, p.name])), [partners]);

  const submitMatch = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.partnerId || !form.periodStart || !form.periodEnd) return;
    setSaving(true);
    clearFlash();
    try {
      const result = await subconInvoicesApi.draftMatch({
        partnerId: form.partnerId,
        invoiceNumber: form.invoiceNumber || undefined,
        periodStart: new Date(form.periodStart).toISOString(),
        periodEnd: new Date(form.periodEnd).toISOString(),
        expectedTotal: form.expectedTotal,
        actualTotal: form.actualTotal,
      });
      showOk(
        result.status === 'matched'
          ? 'Invoice matches what we expected (within tolerance) — no review needed.'
          : 'The invoice differs from what we expected by more than the tolerance. Approve or dispute it in the table below.',
      );
      setForm(EMPTY_MATCH_FORM);
      reload();
      reloadAccuracy();
    } catch (err) {
      showError(errorText(err, 'Could not check the invoice.'));
    } finally {
      setSaving(false);
    }
  };

  const decide = async (invoice: SubconInvoice, decision: 'approve' | 'dispute') => {
    const who = partnerMap[invoice.partnerId] ?? 'the subcontractor';
    const ref = invoice.invoiceNumber ? `invoice ${invoice.invoiceNumber}` : 'this invoice';
    if (decision === 'dispute') {
      const confirmed = await confirm({
        title: `Dispute ${ref}?`,
        message: `${who} billed ${formatPeso(invoice.actualTotal ?? 0)} but we expected ${formatPeso(invoice.expectedTotal ?? 0)}. Disputing marks it as not approved for payment and counts against their accuracy score.`,
        confirmLabel: 'Dispute invoice',
        danger: true,
      });
      if (!confirmed) return;
    }
    setDecidingId(invoice.id);
    clearFlash();
    try {
      await subconInvoicesApi.decide(invoice.id, decision);
      toast(decision === 'approve' ? `Approved ${ref} from ${who}` : `Disputed ${ref} from ${who}`);
      reload();
      reloadAccuracy();
    } catch (err) {
      showError(errorText(err, `Could not ${decision} ${ref}.`));
    } finally {
      setDecidingId(null);
    }
  };

  return (
    <section className="panel">
      <h2>Check subcontractor invoices</h2>
      <p className="text-muted">
        Enter what a subcontractor billed and what you expected. Invoices within tolerance are
        approved automatically; anything else waits below for you to approve or dispute.
      </p>

      <form onSubmit={submitMatch} className="match-form">
        <Select
          label="Subcontractor"
          value={form.partnerId}
          onChange={(e) => setForm({ ...form, partnerId: e.target.value })}
          required
        >
          <option value="">Select subcontractor…</option>
          {partners.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </Select>
        <Input
          label="Invoice number"
          value={form.invoiceNumber}
          onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })}
          placeholder="INV-00123"
        />
        <div className="grid-2">
          <Input
            label="Period start"
            type="date"
            value={form.periodStart}
            onChange={(e) => setForm({ ...form, periodStart: e.target.value })}
            required
          />
          <Input
            label="Period end"
            type="date"
            value={form.periodEnd}
            onChange={(e) => setForm({ ...form, periodEnd: e.target.value })}
            required
          />
        </div>
        <div className="grid-2">
          <Input
            label="Expected total (₱)"
            type="number"
            step="0.01"
            value={form.expectedTotal}
            onChange={(e) => setForm({ ...form, expectedTotal: e.target.value })}
            required
          />
          <Input
            label="Actual total on their invoice (₱)"
            type="number"
            step="0.01"
            value={form.actualTotal}
            onChange={(e) => setForm({ ...form, actualTotal: e.target.value })}
            required
          />
        </div>
        <Button type="submit" loading={saving}>Check invoice</Button>
      </form>

      <FlashMessage flash={flash} />

      <DataTable
        dense
        keyField="id"
        data={invoices ?? []}
        loading={loading}
        loadError={invoicesLoadError}
        emptyMessage="No subcon invoices matched yet."
        columns={[
          {
            key: 'partner',
            header: 'Subcontractor',
            value: (r: SubconInvoice) => partnerMap[r.partnerId] ?? '',
            render: (r: SubconInvoice) => partnerMap[r.partnerId] ?? '—',
          },
          {
            key: 'invoiceNumber',
            header: 'Invoice #',
            value: (r: SubconInvoice) => r.invoiceNumber ?? '',
            render: (r: SubconInvoice) => r.invoiceNumber ?? '—',
          },
          {
            key: 'expectedTotal',
            header: 'Expected',
            value: (r: SubconInvoice) => r.expectedTotal ?? 0,
            render: (r: SubconInvoice) => formatPeso(r.expectedTotal ?? 0),
          },
          {
            key: 'actualTotal',
            header: 'Actual',
            value: (r: SubconInvoice) => r.actualTotal ?? 0,
            render: (r: SubconInvoice) => formatPeso(r.actualTotal ?? 0),
          },
          {
            key: 'variance',
            header: 'Variance',
            value: (r: SubconInvoice) => r.variance ?? 0,
            render: (r: SubconInvoice) => formatPeso(r.variance ?? 0),
          },
          {
            key: 'status',
            header: 'Status',
            value: (r: SubconInvoice) => r.status,
            render: (r: SubconInvoice) => <StatusBadge status={r.status} size="sm" />,
          },
          {
            key: 'firstTimeMatch',
            header: 'First-time',
            value: (r: SubconInvoice) => r.firstTimeMatch ?? false,
            render: (r: SubconInvoice) => (r.firstTimeMatch ? 'Yes' : 'No'),
          },
          {
            key: 'actions',
            header: '',
            sortable: false,
            render: (r: SubconInvoice) =>
              r.status === 'variance_review' ? (
                <div className="row-actions">
                  <Button type="button" size="sm" loading={decidingId === r.id} onClick={() => void decide(r, 'approve')}>Approve</Button>
                  <Button type="button" size="sm" variant="danger" loading={decidingId === r.id} onClick={() => void decide(r, 'dispute')}>Dispute</Button>
                </div>
              ) : (
                '—'
              ),
          },
        ]}
      />

      <h3 className="accuracy-heading">Subcontractor billing accuracy — % of invoices right first time</h3>
      <DataTable
        dense
        keyField="partnerId"
        data={accuracy ?? []}
        emptyMessage="No decided invoices yet."
        columns={[
          { key: 'partnerName', header: 'Subcontractor' },
          { key: 'total', header: 'Total invoices' },
          { key: 'matched', header: 'Matched' },
          { key: 'disputed', header: 'Disputed' },
          { key: 'pending', header: 'Pending review' },
          {
            key: 'firstTimeMatchPct',
            header: '% first-time match',
            value: (r) => r.firstTimeMatchPct ?? null,
            render: (r) => (r.firstTimeMatchPct != null ? `${r.firstTimeMatchPct}%` : '—'),
          },
        ]}
      />

      <style>{`
        .panel {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
          margin-top: var(--space-xl);
        }
        .panel h2 { margin-bottom: var(--space-xs); }
        .match-form {
          display: flex;
          flex-direction: column;
          gap: var(--space-md);
          max-width: 560px;
          margin-bottom: var(--space-lg);
        }
        .accuracy-heading { margin-top: var(--space-xl); margin-bottom: var(--space-sm); font-size: 1rem; }
      `}</style>
    </section>
  );
}
